import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const IconPhone = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="2" width="14" height="20" rx="2"/>
    <line x1="12" y1="18" x2="12" y2="18" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconWarning = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconPlay = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const IconStore = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const LoginPage = () => {
  const { login, register } = useAuth();

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [shakeLogin, setShakeLogin] = useState(false);

  // Register modal state
  const [showRegister, setShowRegister] = useState(false);
  const [regStoreName, setRegStoreName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [isRegLoading, setIsRegLoading] = useState(false);
  const [shakeReg, setShakeReg] = useState(false);

  const loginCardRef = useRef(null);

  const triggerShake = (setShake) => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (!email.trim() || !password.trim()) {
      setLoginError('이메일과 비밀번호를 입력해주세요.');
      triggerShake(setShakeLogin);
      return;
    }

    setIsLoginLoading(true);
    try {
      await login(email.trim(), password);
      // Navigation handled by App.js route redirect
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.';
      setLoginError(msg);
      triggerShake(setShakeLogin);

      // Demo mode: auto-fill mock credentials hint
      if (!err.response) {
        setLoginError('서버에 연결할 수 없습니다. 데모 계정: demo@store.com / demo1234');
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setEmail('demo@store.com');
    setPassword('demo1234');
    setLoginError('');
    setIsLoginLoading(true);
    try {
      await login('demo@store.com', 'demo1234');
    } catch (err) {
      const msg = err.response?.data?.error || '데모 로그인에 실패했습니다.';
      setLoginError(msg);
      triggerShake(setShakeLogin);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');

    if (!regStoreName.trim() || !regEmail.trim() || !regPassword.trim()) {
      setRegError('모든 항목을 입력해주세요.');
      triggerShake(setShakeReg);
      return;
    }
    if (regPassword !== regConfirm) {
      setRegError('비밀번호가 일치하지 않습니다.');
      triggerShake(setShakeReg);
      return;
    }
    if (regPassword.length < 6) {
      setRegError('비밀번호는 6자 이상이어야 합니다.');
      triggerShake(setShakeReg);
      return;
    }

    setIsRegLoading(true);
    try {
      await register(regStoreName.trim(), regEmail.trim(), regPassword);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || '회원가입에 실패했습니다. 다시 시도해주세요.';
      setRegError(msg);
      triggerShake(setShakeReg);
    } finally {
      setIsRegLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-pattern" />

      <div className={`login-card ${shakeLogin ? 'shake' : ''}`} ref={loginCardRef}>
        {/* Logo */}
        <div className="login-logo animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <div className="login-logo-icon" style={{ animation: 'scaleIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) both', animationDelay: '200ms' }}><IconPhone /></div>
          <div className="login-title">스마트오더 관리자</div>
          <div className="login-subtitle">레스토랑 관리 대시보드</div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin}>
          <div className="form-group animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <label className="form-label">이메일</label>
            <div className="input-with-icon">
              <span className="input-icon"><IconMail /></span>
              <input
                type="email"
                className="form-input"
                placeholder="이메일을 입력하세요"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="form-group animate-fade-in-up" style={{ animationDelay: '260ms' }}>
            <label className="form-label">비밀번호</label>
            <div className="input-with-icon">
              <span className="input-icon"><IconLock /></span>
              <input
                type="password"
                className="form-input"
                placeholder="비밀번호를 입력하세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          {loginError && (
            <div className="form-error animate-fade-in-down" style={{ marginBottom: 14, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconWarning /> {loginError}
            </div>
          )}

          <button
            type="submit"
            className="login-btn"
            disabled={isLoginLoading}
          >
            {isLoginLoading ? (
              <>
                <span className="spinner" />
                <span>로그인 중...</span>
              </>
            ) : (
              '로그인'
            )}
          </button>
        </form>

        {/* Demo button */}
        <button
          type="button"
          onClick={handleDemoLogin}
          disabled={isLoginLoading}
          style={{
            width: '100%',
            marginTop: 10,
            padding: '11px',
            background: 'rgba(191,155,48,0.08)',
            border: '1.5px dashed rgba(191,155,48,0.3)',
            borderRadius: '8px',
            color: 'var(--primary)',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(191,155,48,0.14)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(191,155,48,0.08)'}
        >
          <IconPlay /> 데모로 시작하기
        </button>

        {/* Register link */}
        <div className="login-register-link">
          계정이 없으신가요?
          <button type="button" onClick={() => setShowRegister(true)}>
            무료로 시작하기
          </button>
        </div>
      </div>

      {/* Register Modal */}
      {showRegister && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowRegister(false); }}>
          <div className={`modal ${shakeReg ? 'shake' : ''}`} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconPlay /> 무료로 시작하기
              </h3>
              <button className="modal-close" onClick={() => setShowRegister(false)}><IconX /></button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label className="form-label">매장 이름</label>
                  <div className="input-with-icon">
                    <span className="input-icon"><IconStore /></span>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="예: 맛있는 한식당"
                      value={regStoreName}
                      onChange={(e) => setRegStoreName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">이메일</label>
                  <div className="input-with-icon">
                    <span className="input-icon"><IconMail /></span>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="이메일을 입력하세요"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">비밀번호</label>
                  <div className="input-with-icon">
                    <span className="input-icon"><IconLock /></span>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="6자 이상 입력하세요"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">비밀번호 확인</label>
                  <div className="input-with-icon">
                    <span className="input-icon"><IconLock /></span>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="비밀번호를 다시 입력하세요"
                      value={regConfirm}
                      onChange={(e) => setRegConfirm(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {regError && (
                  <div className="form-error" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconWarning /> {regError}
                  </div>
                )}

                <button
                  type="submit"
                  className="login-btn"
                  disabled={isRegLoading}
                >
                  {isRegLoading ? (
                    <>
                      <span className="spinner" />
                      <span>처리 중...</span>
                    </>
                  ) : (
                    '회원가입'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
